import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseRemoteConfigService, SentryErrorHandler } from '@core/services/common';
import { LogService } from '@core/services/core/log.service';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class HttpInterceptorError implements HttpInterceptor {

  constructor(private log: LogService, private firebaseConfig: FirebaseRemoteConfigService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // local assets in browser are not intercepted
    if (!req.url.startsWith('http')) { return next.handle(req); }
    return next.handle(req).pipe(
      catchError(httpError => {
        this.notifyErrorIfRequired(httpError, req);
        return throwError(httpError);
      })
    );
  }

  private async notifyErrorIfRequired(httpError: HttpErrorResponse, req: HttpRequest<any>) {
    if (httpError instanceof HttpErrorResponse) {
      this.notifyAndManageUnauthorizedError(httpError, req);
      this.notifyAndManageTimeoutError(httpError);
      this.notifyAndManageConnectionError(httpError);
      await this.notifyAndManageBackendRecognizedError(httpError);
      // unhandled/unrecognized/unexpected errors
      if (!(httpError as any).handledByInterceptor ) {
        SentryErrorHandler.sendServerError(httpError);
      }
    }
  }

  private notifyAndManageUnauthorizedError(httpError: any, req: HttpRequest<any>) {
    const isPrivateArea =
      req.url.indexOf('middlewarePrivateOIMServices') >= 0 ||
      req.url.indexOf('middlewarePrivateTeCuidamosServices') >= 0;
    if (isPrivateArea && [401, 403].includes(httpError?.status)) {
      // Ignore 401 report if request is an unauthorized user login action
      this.log.d(this, `Ignore Unauthorized error: ${httpError.status} ${httpError.url}`);
      httpError.handledByInterceptor = true;
    }
  }

  private notifyAndManageTimeoutError(httpError: any) {
    if (httpError?.status === 408 || httpError?.status === 504) {
      const errorCode = 'TIMEOUT';
      SentryErrorHandler.sendServerErrorHandled(httpError, errorCode, 'debug');
      httpError.handledByInterceptor = true;
    }
  }

  private notifyAndManageConnectionError(httpError: any) {
    if (httpError?.status === 0) {
      const errorCode = navigator.onLine ? 'CONNECTION_ERROR' : 'USER_CONNECTION_ERROR';
      const level = navigator.onLine ? 'warning' : 'log';
      SentryErrorHandler.sendServerErrorHandled(httpError, errorCode, level);
      httpError.handledByInterceptor = true;
    }
  }

  private async notifyAndManageBackendRecognizedError(httpError: any) {
    if (await this.isKnownError(httpError)) {
      this.log.d(this, `Ignore known error: ${httpError.status} ${httpError.url}`, httpError);
      httpError.handledByInterceptor = true;
    }
  }

  private async isKnownError(httpError: HttpErrorResponse) {
    const value = await this.firebaseConfig.getConfigValue('httpErrorSentryWhitelist').catch(() => undefined);
    const knownErrors: { httpStatusCode: number, url: string, errorCode?: string, errorKey?: string, errorType?: string } [] =
      !!value?.asString() ? JSON.parse(value.asString()) : [];
    return knownErrors.some(err => {
      const containsUrl = httpError.url.includes(err.url);
      const sameStatusCode = err.httpStatusCode === httpError.status;
      const serverErrorInfo = httpError.error?.data || httpError.error;
      const sameErrorCode = err.errorCode ? err.errorCode === serverErrorInfo?.errorCode : true;
      const sameErrorKey = err.errorKey ? err.errorKey === serverErrorInfo?.errorKey : true;
      return containsUrl && sameStatusCode && sameErrorCode && sameErrorKey;
    });

  }


}
